import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { all_routes } from '../../../router/all_routes';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import reportService from '../../../services/reports/report.service';
import { EmployeeReportDto, AgeDetails } from '../../../types/report.types';
import { Table, Tag, Tooltip, Select, Button } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { handleApiError } from '../../../utils/errorHandler';
import { createPdf, exportToExcel, normalizeTurkishChars } from '../../../utils/exportUtils';
import FooterSection from '../../shared/components/footerSection';
import { PermissionType } from '../../../types/user.types';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';


const EmployeeReport = () => {
    const [reportData, setReportData] = useState<EmployeeReportDto | null>(null);
    const [filteredData, setFilteredData] = useState<AgeDetails[]>([]);
    const [loading, setLoading] = useState(true);
    const [branches, setBranches] = useState<string[]>([]);
    const [departments, setDepartments] = useState<string[]>([]);
    const [selectedBranch, setSelectedBranch] = useState<string | undefined>(undefined);
    const [selectedDepartment, setSelectedDepartment] = useState<string | undefined>(undefined);
    /** Backend'e gönderilen çalışan durumu: 'all' | 'active' | 'active,resigned' */
    const [statusFilter, setStatusFilter] = useState<string>('active');
    const { t } = useTranslation();
    const { user } = useSelector((state: RootState) => state.auth);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const statusParam = statusFilter === 'all' ? undefined : statusFilter;
                const data = await reportService.getEmployeeReport(statusParam);
                setReportData(data);

                const uniqueBranches = Array.from(new Set(data.ageDetails.map(item => item.branch).filter(Boolean))) as string[];
                setBranches(uniqueBranches);

                const uniqueDepartments = Array.from(new Set(data.ageDetails.map(item => item.department).filter((dept): dept is string => Boolean(dept))));
                setDepartments(uniqueDepartments);

                filterData(data);
            } catch (error: any) {
                handleApiError(error, t);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [statusFilter]);

    // Filtre değiştiğinde veriyi güncelle
    useEffect(() => {
        if (reportData) {
            filterData(reportData);
        }
    }, [selectedBranch, selectedDepartment, reportData]);

    // Filtreleme fonksiyonu - UI'da filtreleme yapıyor
    // Hem şube hem departman seçilirse: "Bu şubedeki şu departman" mantığıyla çalışır
    const filterData = (data: EmployeeReportDto) => {
        let filtered = [...data.ageDetails];
        
        // Şube filtresi: Seçili şubedeki çalışanları filtrele
        if (selectedBranch) {
            filtered = filtered.filter(item => item.branch === selectedBranch);
        }
        
        // Departman filtresi: Seçili departmandaki çalışanları filtrele
        // Eğer şube de seçilmişse, önce şube filtresi uygulanmış olur, sonra departman filtresi uygulanır
        // Sonuç: "Bu şubedeki şu departman" çalışanları
        if (selectedDepartment) {
            filtered = filtered.filter(item => item.department === selectedDepartment);
        }
        
        setFilteredData(filtered);
    };

    // Filtrelenmiş veriye göre grafik verilerini hesapla
    const calculateFilteredChartData = (filtered: AgeDetails[]) => {
        if (!filtered || filtered.length === 0) {
            return {
                genderDistribution: { male: 0, female: 0 },
                maritalStatusDistribution: { married: 0, single: 0 },
                educationDistribution: { primary: 0, secondary: 0, bachelor: 0, master: 0, phd: 0 }
            };
        }

        // Cinsiyet dağılımı
        const genderCounts = { male: 0, female: 0 };
        filtered.forEach(item => {
            if (item.gender === 'male') genderCounts.male++;
            else if (item.gender === 'female') genderCounts.female++;
        });

        // Medeni durum dağılımı
        const maritalCounts = { married: 0, single: 0 };
        filtered.forEach(item => {
            if (item.maritalStatus === 'married') maritalCounts.married++;
            else if (item.maritalStatus === 'single') maritalCounts.single++;
        });

        // Eğitim seviyesi dağılımı
        const educationCounts: { primary: number; secondary: number; bachelor: number; master: number; phd: number } = { primary: 0, secondary: 0, bachelor: 0, master: 0, phd: 0 };
        filtered.forEach(item => {
            if (item.educationLevel && item.educationLevel in educationCounts) {
                educationCounts[item.educationLevel as keyof typeof educationCounts]++;
            }
        });

        return {
            genderDistribution: genderCounts,
            maritalStatusDistribution: maritalCounts,
            educationDistribution: educationCounts
        };
    };

    // Filtrelenmiş grafik verileri
    const filteredChartData = useMemo(() => {
        const isFilterActive = selectedBranch || selectedDepartment;
        if (!isFilterActive) {
            return null; // Filtre yoksa orijinal veriyi kullan
        }
        return calculateFilteredChartData(filteredData);
    }, [filteredData, selectedBranch, selectedDepartment]);

    // Filtre sıfırlama
    const resetFilters = () => {
        setSelectedBranch(undefined);
        setSelectedDepartment(undefined);
        setStatusFilter('active');
    };

    // Çalışma süresini metin olarak formatlama
    const formatWorkDuration = (duration: { days: number; hours: number; minutes: number }) => {
        // Günleri yıl ve kalan günlere dönüştür
        let years = 0;
        let remainingDays = duration.days;
        
        if (remainingDays >= 365) {
            years = Math.floor(remainingDays / 365);
            remainingDays = remainingDays % 365;
        }
        
        const parts = [];
        if (years > 0) parts.push(`${years} ${t('employeeReport.years')}`);
        if (remainingDays > 0) parts.push(`${remainingDays} ${t('employeeReport.days')}`);
        //if (duration.hours > 0) parts.push(`${duration.hours} saat`);
        //if (duration.minutes > 0) parts.push(`${duration.minutes} dakika`);
        return parts.join(', ');
    };

    // Çalışma süresini sıralamak için tam dakika cinsinden toplam süre
    const getTotalMinutes = (duration: { days: number; hours: number; minutes: number }) => {
        return duration.days * 24 * 60 + duration.hours * 60 + duration.minutes;
    };

    // Tablo için sütun tanımları
    const columns: ColumnsType<AgeDetails> = [
        {
            title: t('employeeReport.name'),
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: t('employeeReport.age'),
            dataIndex: 'age',
            key: 'age',
            sorter: (a, b) => a.age - b.age,
            render: (age) => (
                <Tag color="geekblue">{age}</Tag>
            )
        },
        {
            title: t('employeeReport.branch'),
            dataIndex: 'branch',
            key: 'branch',
            filters: branches.map(branch => ({ text: branch, value: branch })),
            onFilter: (value, record) => record.branch === value,
        },
        {
            title: t('employeeReport.workDuration'),
            dataIndex: 'workDuration',
            key: 'workDuration',
            sorter: (a, b) => getTotalMinutes(a.workDuration) - getTotalMinutes(b.workDuration),
            render: (workDuration) => {
                // Günleri yıl ve kalan günlere dönüştür
                let years = 0;
                let remainingDays = workDuration.days;
                
                if (remainingDays >= 365) {
                    years = Math.floor(remainingDays / 365);
                    remainingDays = remainingDays % 365;
                }
                
                let tooltipText = "";
                if (years > 0) tooltipText += `${years} ${t('employeeReport.years')}, `;
                if (remainingDays > 0) tooltipText += `${remainingDays} ${t('employeeReport.days')}, `;
                tooltipText += `${workDuration.hours} ${t('employeeReport.hours')}, ${workDuration.minutes} ${t('employeeReport.minutes')}`;
                
                return (
                    <Tooltip title={tooltipText}>
                        <span>{formatWorkDuration(workDuration)}</span>
                    </Tooltip>
                );
            }
        },
    ];

    // Gender Distribution Chart
    const genderChartOptions: ApexOptions = {
        chart: {
            type: 'pie' as const,
            height: 300
        },
        labels: [t('employeeReport.male'), t('employeeReport.female')],
        colors: ['#4318FF', '#6AD2FF'],
        legend: {
            position: 'bottom'
        }
    };

    // Marital Status Chart
    const maritalChartOptions: ApexOptions = {
        chart: {
            type: 'pie' as const,
            height: 300
        },
        labels: [t('employeeReport.married'), t('employeeReport.unMarried')],
        colors: ['#05CD99', '#FFB547'],
        legend: {
            position: 'bottom'
        }
    };

    // Education Distribution Chart
    const educationChartOptions: ApexOptions = {
        chart: {
            type: 'donut' as const,
            height: 300
        },
        labels: [
            t('employeeReport.educationLevels.primary'),
            t('employeeReport.educationLevels.secondary'),
            t('employeeReport.educationLevels.bachelor'),
            t('employeeReport.educationLevels.master'),
            t('employeeReport.educationLevels.phd')
        ],
        colors: ['#FF3B30', '#FF9500', '#007AFF', '#34C759', '#AF52DE'],
        legend: {
            position: 'bottom'
        },
        plotOptions: {
            pie: {
                donut: {
                    size: '65%'
                }
            }
        }
    };

    // Branch Distribution Chart - filtrelenmiş veriye göre
    const getBranchDistribution = () => {
        if (!filteredData || filteredData.length === 0) {
            return { categories: [], data: [] };
        }
        
        const branchCounts: { [key: string]: number } = {};
        filteredData.forEach(detail => {
            if (detail?.branch) {
                branchCounts[detail.branch] = (branchCounts[detail.branch] || 0) + 1;
            }
        });

        return {
            categories: Object.keys(branchCounts),
            data: Object.values(branchCounts)
        };
    };


    // Branch chart options - dinamik olarak güncellenecek
    const getBranchChartOptions = (categories: string[]): ApexOptions => ({
        chart: {
            type: 'bar' as const,
            height: 350
        },
        plotOptions: {
            bar: {
                borderRadius: 4,
                horizontal: true,
            }
        },
        colors: ['#4318FF'],
        xaxis: {
            categories: categories || []
        }
    });

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Breadcrumb */}
                <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                    <div className="my-auto mb-2">
                        <h2 className="mb-1">{t('employeeReport.pageTitle')}</h2>
                        <nav>
                            <ol className="breadcrumb mb-0">
                                <li className="breadcrumb-item">
                                    <Link to={all_routes.adminDashboard}>
                                        <i className="ti ti-smart-home" />
                                    </Link>
                                </li>
                                <li className="breadcrumb-item">{t('employeeReport.breadcrumb.reports')}</li>
                                <li className="breadcrumb-item active" aria-current="page">
                                    {t('employeeReport.breadcrumb.employeeReport')}
                                </li>
                            </ol>
                        </nav>
                    </div>
                    {user?.permissions?.includes(PermissionType.EXPORT) && (
                    <div className="d-flex my-xl-auto right-content align-items-center">
                        <div className="mb-2">
                            <div className="dropdown me-2">
                                <Link
                                    to="#"
                                    className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                                    data-bs-toggle="dropdown"
                                >
                                    <i className="ti ti-file-export me-1" />
                                    {t('common.buttons.export')}
                                </Link>
                                <ul className="dropdown-menu dropdown-menu-end p-3">
                                    <li>
                                    <Link
                                        to="#"
                                        className="dropdown-item rounded-2 d-flex align-items-center py-2"
                                        onClick={() => {
                                            // Çalışan verileri için sütun tanımları
                                            type EmployeeDataType = {
                                                name: string;
                                                age: number;
                                                branch: string;
                                                workDuration: string;
                                            };

                                            // PDF için sütun tanımları - Landscape A4 genişliğine göre optimize edildi (297mm, margin'lerle ~281mm)
                                            const columnsForPdf = [
                                                { header: normalizeTurkishChars('Ad Soyad'), property: 'name' as keyof EmployeeDataType, width: 90, align: 'left' as const },
                                                { header: normalizeTurkishChars('Yaş'), property: 'age' as keyof EmployeeDataType, width: 30, align: 'center' as const },
                                                { header: normalizeTurkishChars('Şube'), property: 'branch' as keyof EmployeeDataType, width: 80, align: 'left' as const },
                                                { header: normalizeTurkishChars('Çalışma Süresi'), property: 'workDuration' as keyof EmployeeDataType, width: 70, align: 'left' as const }
                                            ];

                                            // PDF için formatlanmış veri
                                            const formattedData = filteredData.map(employee => {
                                                // Çalışma süresini metin olarak formatlama
                                                let formattedDuration = '';
                                                const duration = employee.workDuration;
                                                
                                                // Günleri yıl ve kalan günlere dönüştür
                                                let years = 0;
                                                let remainingDays = duration.days;
                                                
                                                if (remainingDays >= 365) {
                                                    years = Math.floor(remainingDays / 365);
                                                    remainingDays = remainingDays % 365;
                                                }
                                                
                                                const parts = [];
                                                if (years > 0) parts.push(`${years} yil`);
                                                if (remainingDays > 0) parts.push(`${remainingDays} gun`);
                                                if (duration.hours > 0) parts.push(`${duration.hours} saat`);
                                                if (duration.minutes > 0) parts.push(`${duration.minutes} dakika`);
                                                formattedDuration = parts.join(', ');

                                                return {
                                                    name: normalizeTurkishChars(employee.name),
                                                    age: employee.age,
                                                    branch: normalizeTurkishChars(employee.branch),
                                                    workDuration: formattedDuration
                                                };
                                            });

                                            // Filtre açıklaması
                                            let description = normalizeTurkishChars('Çalışan Raporu');
                                            
                                            if (selectedDepartment) {
                                                description += ` - Departman: ${normalizeTurkishChars(selectedDepartment)}`;
                                            }
                                            
                                            if (selectedBranch) {
                                                description += ` - Şube: ${normalizeTurkishChars(selectedBranch)}`;
                                            }

                                            // createPdf fonksiyonunu çağır
                                            createPdf(formattedData, {
                                                title: normalizeTurkishChars('Çalışan Raporu'),
                                                description: description,
                                                headerColor: '#2c3e50', // Koyu lacivert
                                                accentColor: '#3498db', // Mavi
                                                columns: columnsForPdf,
                                                showStatistics: false, // İstatistik kutuları gösterme
                                                footer: {
                                                    text: `© ${new Date().getFullYear()} ${normalizeTurkishChars('DINOX')} - ${normalizeTurkishChars('Tum Haklari Saklidir')}`,
                                                    showPageNumbers: true
                                                }
                                            });
                                        }}
                                    >
                                        <i className="ti ti-file-type-pdf me-2 text-danger" />
                                        {t('common.buttons.pdf')}
                                    </Link>
                                    </li>
                                    <li className="mt-1">
                                    <Link
                                        to="#"
                                        className="dropdown-item rounded-2 d-flex align-items-center py-2"
                                        onClick={() => {
                                            // Excel için sütun tanımları
                                            type EmployeeExcelType = {
                                                name: string;
                                                age: number;
                                                branch: string;
                                                department: string;
                                                workDuration: string;
                                                yearsOfExperience: number;
                                            };

                                            const columnsForExcel = [
                                                { header: 'Ad Soyad', property: 'name' as keyof EmployeeExcelType, width: 30 },
                                                { header: 'Yaş', property: 'age' as keyof EmployeeExcelType, width: 10 },
                                                { header: 'Şube', property: 'branch' as keyof EmployeeExcelType, width: 20 },
                                                { header: 'Departman', property: 'department' as keyof EmployeeExcelType, width: 20 },
                                                { header: 'Çalışma Süresi', property: 'workDuration' as keyof EmployeeExcelType, width: 40 },
                                                { header: 'Tecrübe (Yıl)', property: 'yearsOfExperience' as keyof EmployeeExcelType, width: 15 }
                                            ];

                                            // Excel için formatlanmış veri
                                            const formattedData = filteredData.map(employee => {
                                                // Çalışma süresini metin olarak formatlama
                                                let formattedDuration = '';
                                                const duration = employee.workDuration;
                                                
                                                // Günleri yıl ve kalan günlere dönüştür
                                                let years = 0;
                                                let remainingDays = duration.days;
                                                
                                                if (remainingDays >= 365) {
                                                    years = Math.floor(remainingDays / 365);
                                                    remainingDays = remainingDays % 365;
                                                }
                                                
                                                const parts = [];
                                                if (years > 0) parts.push(`${years} yıl`);
                                                if (remainingDays > 0) parts.push(`${remainingDays} gün`);
                                                if (duration.hours > 0) parts.push(`${duration.hours} saat`);
                                                if (duration.minutes > 0) parts.push(`${duration.minutes} dakika`);
                                                formattedDuration = parts.join(', ');

                                                return {
                                                    name: employee.name,
                                                    age: employee.age,
                                                    branch: employee.branch,
                                                    department: employee.department || '-',
                                                    workDuration: formattedDuration,
                                                    yearsOfExperience: years
                                                };
                                            });

                                            // Excel dosyasını oluştur
                                            exportToExcel(formattedData, 'Calisan_Raporu', columnsForExcel as any);
                                        }}
                                    >
                                        <i className="ti ti-file-type-xls me-2 text-success" />
                                        {t('common.buttons.excel')}
                                    </Link>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    )}
                </div>

                {/* Filtreler */}
                <div className="card mb-3">
                    <div className="card-body py-3">
                        <div className="d-flex flex-wrap justify-content-between align-items-end gap-3">
                            <div className="d-flex flex-wrap gap-3 align-items-end">
                                <div>
                                    <label className="form-label mb-2 fs-13 fw-semibold text-dark">
                                        <i className="ti ti-user-check me-1 text-success"></i>
                                        Çalışan Durumu
                                    </label>
                                    <Select
                                        value={statusFilter}
                                        onChange={(value) => setStatusFilter(value)}
                                        style={{ width: 260, boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}
                                        options={[
                                            { value: 'active', label: 'Sadece aktif çalışanlar' },
                                            { value: 'active,resigned', label: 'Aktif + İşten ayrılan çalışanlar' }
                                        ]}
                                    />
                                </div>
                                <div>
                                    <label className="form-label mb-2 fs-13 fw-semibold text-dark">
                                        <i className="ti ti-building-skyscraper me-1 text-primary"></i>
                                        Departman Filtresi
                                    </label>
                                    <Select
                                        placeholder="Tüm Departmanlar"
                                        allowClear
                                        style={{ width: 220, boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}
                                        onChange={(value) => setSelectedDepartment(value)}
                                        value={selectedDepartment}
                                        suffixIcon={<i className="ti ti-chevron-down text-primary"></i>}
                                        className="filter-select"
                                        dropdownStyle={{ borderRadius: '8px', boxShadow: '0 6px 16px rgba(0,0,0,0.1)' }}
                                    >
                                        {departments.map(dept => (
                                            <Select.Option key={dept} value={dept}>
                                                <div className="d-flex align-items-center">
                                                    <span className="me-2 rounded-circle d-inline-block" style={{ width: "8px", height: "8px", backgroundColor: "#1890ff" }}></span>
                                                    {dept}
                                                </div>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>
                                <div>
                                    <label className="form-label mb-2 fs-13 fw-semibold text-dark">
                                        <i className="ti ti-map-pin me-1 text-danger"></i>
                                        Şube Filtresi
                                    </label>
                                    <Select
                                        placeholder="Tüm Şubeler"
                                        allowClear
                                        style={{ width: 220, boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}
                                        onChange={(value) => setSelectedBranch(value)}
                                        value={selectedBranch}
                                        suffixIcon={<i className="ti ti-chevron-down text-danger"></i>}
                                        className="filter-select"
                                        dropdownStyle={{ borderRadius: '8px', boxShadow: '0 6px 16px rgba(0,0,0,0.1)' }}
                                    >
                                        {branches.map(branch => (
                                            <Select.Option key={branch} value={branch}>
                                                <div className="d-flex align-items-center">
                                                    <span className="me-2 rounded-circle d-inline-block" style={{ width: "8px", height: "8px", backgroundColor: "#ff4d4f" }}></span>
                                                    {branch}
                                                </div>
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>
                            </div>
                            <div>
                                <Button
                                    type="default"
                                    icon={<FilterOutlined />}
                                    onClick={resetFilters}
                                    className="d-flex align-items-center"
                                    style={{
                                        borderRadius: '8px',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                                        background: 'white',
                                        border: '1px solid #e9ecef'
                                    }}
                                >
                                    <span className="ms-1">Filtreleri Sıfırla</span>
                                </Button>
                            </div>
                        </div>

                        {/* Aktif Filtreler */}
                        {(selectedDepartment || selectedBranch || statusFilter !== 'active') && (
                            <div className="mt-3 pt-2 border-top">
                                <div className="d-flex align-items-center flex-wrap">
                                    <small className="text-muted me-2">Aktif Filtreler:</small>
                                    <div className="d-flex gap-2 flex-wrap">
                                        {statusFilter !== 'active' && (
                                            <Tag
                                                color="green"
                                                closable
                                                onClose={() => setStatusFilter('active')}
                                                style={{ borderRadius: '20px', padding: '2px 10px' }}
                                            >
                                                <i className="ti ti-user-check me-1"></i>
                                                {statusFilter === 'active,resigned' ? 'Aktif + İşten Ayrılan' : statusFilter}
                                            </Tag>
                                        )}
                                        {selectedDepartment && (
                                            <Tag
                                                color="blue"
                                                closable
                                                onClose={() => setSelectedDepartment(undefined)}
                                                style={{ borderRadius: '20px', padding: '2px 10px' }}
                                            >
                                                <i className="ti ti-building-skyscraper me-1"></i>
                                                {selectedDepartment}
                                            </Tag>
                                        )}
                                        {selectedBranch && (
                                            <Tag
                                                color="red"
                                                closable
                                                onClose={() => setSelectedBranch(undefined)}
                                                style={{ borderRadius: '20px', padding: '2px 10px' }}
                                            >
                                                <i className="ti ti-map-pin me-1"></i>
                                                {selectedBranch}
                                            </Tag>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status">

                        </div>
                    </div>
                ) : reportData ? (
                    <div className="row">
                        {/* Cinsiyet ve Medeni Durum Dağılımı */}
                        <div className="col-xl-6 d-flex">
                            <div className="card flex-fill">
                                <div className="card-header">
                                    <h5 className="card-title">{t('employeeReport.genderDistribution')}</h5>
                                </div>
                                <div className="card-body">
                                    {loading ? (
                                        <div className="text-center p-3">
                                            <div className="spinner-border text-primary" role="status"></div>
                                        </div>
                                    ) : (() => {
                                        const isFilterActive = selectedBranch || selectedDepartment;
                                        const chartData = isFilterActive ? filteredChartData?.genderDistribution : reportData?.genderDistribution;
                                        const total = chartData ? (chartData.male || 0) + (chartData.female || 0) : 0;
                                        
                                        return total > 0 ? (
                                            <ReactApexChart
                                                options={genderChartOptions}
                                                series={[
                                                    chartData?.male || 0, 
                                                    chartData?.female || 0
                                                ]}
                                                type="pie"
                                                height={300}
                                            />
                                        ) : (
                                            <div className="text-center p-5">
                                                <i className="ti ti-inbox fs-48 text-muted mb-3"></i>
                                                <p className="text-muted mb-0">Veri Yok</p>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-6 d-flex">
                            <div className="card flex-fill">
                                <div className="card-header">
                                    <h5 className="card-title">{t('employeeReport.maritalStatusDistribution')}</h5>
                                </div>
                                <div className="card-body">
                                    {loading ? (
                                        <div className="text-center p-3">
                                            <div className="spinner-border text-primary" role="status"></div>
                                        </div>
                                    ) : (() => {
                                        const isFilterActive = selectedBranch || selectedDepartment;
                                        const chartData = isFilterActive ? filteredChartData?.maritalStatusDistribution : reportData?.maritalStatusDistribution;
                                        const total = chartData ? (chartData.married || 0) + (chartData.single || 0) : 0;
                                        
                                        return total > 0 ? (
                                            <ReactApexChart
                                                options={maritalChartOptions}
                                                series={[
                                                    chartData?.married || 0, 
                                                    chartData?.single || 0
                                                ]}
                                                type="pie"
                                                height={300}
                                            />
                                        ) : (
                                            <div className="text-center p-5">
                                                <i className="ti ti-inbox fs-48 text-muted mb-3"></i>
                                                <p className="text-muted mb-0">Veri Yok</p>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>

                        {/* Eğitim Seviyesi Dağılımı */}
                        <div className="col-xl-6 d-flex">
                            <div className="card flex-fill">
                                <div className="card-header">
                                    <h5 className="card-title">{t('employeeReport.educationDistribution')}</h5>
                                </div>
                                <div className="card-body">
                                    {loading ? (
                                        <div className="text-center p-3">
                                            <div className="spinner-border text-primary" role="status"></div>
                                        </div>
                                    ) : (() => {
                                        const isFilterActive = selectedBranch || selectedDepartment;
                                        const eduData = isFilterActive ? filteredChartData?.educationDistribution : reportData?.educationDistribution;
                                        const total = eduData ? (eduData.primary || 0) + (eduData.secondary || 0) + (eduData.bachelor || 0) + (eduData.master || 0) + (eduData.phd || 0) : 0;
                                        
                                        return total > 0 && eduData ? (
                                            <ReactApexChart
                                                options={educationChartOptions}
                                                series={[
                                                    eduData.primary || 0,
                                                    eduData.secondary || 0,
                                                    eduData.bachelor || 0,
                                                    eduData.master || 0,
                                                    eduData.phd || 0
                                                ]}
                                                type="donut"
                                                height={300}
                                            />
                                        ) : (
                                            <div className="text-center p-5">
                                                <i className="ti ti-inbox fs-48 text-muted mb-3"></i>
                                                <p className="text-muted mb-0">Veri Yok</p>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>

                        {/* Şube Bazlı Dağılım */}
                        <div className="col-xl-6 d-flex">
                            <div className="card flex-fill">
                                <div className="card-header">
                                    <h5 className="card-title">{t('employeeReport.branchBasedDistribution')}</h5>
                                </div>
                                <div className="card-body">
                                    {loading ? (
                                        <div className="text-center p-3">
                                            <div className="spinner-border text-primary" role="status"></div>
                                        </div>
                                    ) : (() => {
                                        const branchDist = getBranchDistribution();
                                        return branchDist.data.length > 0 ? (
                                            <ReactApexChart
                                                options={getBranchChartOptions(branchDist.categories)}
                                                series={[{
                                                    name: t('employeeReport.employeeCount'),
                                                    data: branchDist.data
                                                }]}
                                                type="bar"
                                                height={350}
                                            />
                                        ) : (
                                            <div className="text-center p-5">
                                                <i className="ti ti-inbox fs-48 text-muted mb-3"></i>
                                                <p className="text-muted mb-0">Veri Yok</p>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>

                        {/* Çalışan Detay Tablosu - Ant Design Table */}
                        <div className="col-xl-12">
                            <div className="card">
                                <div className="card-header">
                                 
                                </div>
                                <div className="card-body">
                                    <Table
                                        columns={columns}
                                        dataSource={filteredData}
                                        rowKey="userId"
                                        pagination={{ 
                                            pageSize: 10,
                                        }}
                                        bordered
                                        size="middle"
                                        loading={loading}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="alert alert-danger">
                        {t('employeeReport.defaultErrorMessage')}
                    </div>
                )}
            </div>
            <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                <FooterSection />
            </div>
        </div>
    );
};

export default EmployeeReport; 